<template>
    <div :class="cn('flex w-full flex-col', $props.class, errorMessage && 'is-invalid')">
        <div v-if="label" class="mb-2 flex items-center gap-2">
            <Label :class="cn(errorMessage && '')" :for="idAttr">
                {{ label }}
            </Label>
        </div>

        <div class="relative">
            <div
                v-if="$slots['icon']"
                class="text-muted-foreground absolute left-2 top-1/2 -translate-y-1/2">
                <slot name="icon"></slot>
            </div>

            <Input
                v-if="type !== 'textarea'"
                :class="cn($slots['icon'] && 'ps-8', 'text-midGrey', inputClass)"
                :name
                :placeholder
                :type="inputType"
                :disabled
                :model-value
                :model-modifiers
                :readonly
                @update:model-value="$emit('update:modelValue', $event)" />
            <Textarea
                v-else
                :class="cn($slots['icon'] && 'ps-8', 'text-midGrey', inputClass)"
                :name
                :placeholder
                :type="inputType"
                :disabled
                :model-value
                :model-modifiers
                @update:model-value="$emit('update:modelValue', $event)" />

            <Button
                v-if="type === 'password'"
                @click="togglePwdReveal"
                variant="ghost"
                size="icon"
                type="button"
                tabindex="-1"
                class="absolute end-3 top-1/2 h-auto w-auto -translate-y-1/2 p-1">
                <EyeOffIcon v-if="!isRevealingPwd" class="text-muted-foreground h-4 w-4">
                </EyeOffIcon>
                <EyeIcon v-else class="text-muted-foreground h-4 w-4"> </EyeIcon>
            </Button>
        </div>

        <p v-if="errorMessage" class="error-message text-destructive text-sm">{{ errorMessage }}</p>
    </div>
</template>

<script setup lang="ts">
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { EyeIcon, EyeOffIcon } from 'lucide-vue-next'
import { computed, ref } from 'vue'
import type { CommonProps } from './types'
import kebabCase from 'lodash/kebabCase'
import { Textarea } from '@/components/ui/textarea'

interface Props {
    errorMessage?: string
    modelValue?: string
    modelModifiers?: { lazy: boolean }
    inputClass?: string
}

const props = withDefaults(defineProps<CommonProps & Props>(), {
    type: 'text',
})

const idAttr = computed(() => {
    return props.id ? props.id : 'txt-' + kebabCase(props.name || '')
})

defineEmits<{
    (e: 'update:modelValue', value: string | number): void
}>()

const togglePwdReveal = () => {
    isRevealingPwd.value = !isRevealingPwd.value
}

const isRevealingPwd = ref(false)

const inputType = computed(() => {
    if (props.type !== 'password') return props.type

    return isRevealingPwd.value ? 'text' : 'password'
})
</script>

<style scoped></style>
